import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Button, Grid, Switch, FormControlLabel } from "@mui/material";
import { ArrowBack, EditNote } from "@mui/icons-material";
import { CleaningRouteList } from "../../redux/cleaning/state";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import { axiosGet } from "../../util/axios";
import { OpenSnackBarWithResultFail } from "../../redux/common/snackBar/thunk";
import { initCleaningDetailRouteThunk } from "../../redux/cleaning/thunk";
import FormDialog from "../common/FormDialog";
import CleaningDetailRoute from "./CleaningDetailRoute";
import { ButtonTextGrid } from "../..";

interface ContractRecord {
    cleaning_id: number;
    bin_id: number;
    bin_full_name: string;
    contract_id: number;
    contract: string;
    attendance_date: string;
    people_counter_number: number;
    update_date: string;
    create_date: string;
    route_cleaning_id: number;
}

interface ContractGroup {
    contract_id: number;
    contract: string;
    records: ContractRecord[];
}

interface ContractRow {
    id: number;
    contract_id: number;
    contract: string;
    record_count: number;
    bin_count: number;
}

function CleaningRoute() {
    const dispatch = useDispatch<IRootThunkDispatch>();
    const { cleaningRouteList, clickDate } = useSelector(
        (state: IRootState) => state.cleaningState
    );
    const [openDetailRouteDialog, setOpenCleaningRouteDialog] =
        useState<boolean>(false);
    const [viewByContract, setViewByContract] = useState<boolean>(false);
    const [contractList, setContractList] = useState<ContractGroup[]>([]);
    const [contractFetched, setContractFetched] = useState<boolean>(false);
    const [selectedContract, setSelectedContract] = useState<ContractGroup | null>(null);
    const [contractDetailLoading, setContractDetailLoading] = useState<boolean>(false);

    async function handleClickRouteCleaningDetail(
        cleaningRouteList: CleaningRouteList
    ) {
        dispatch(OpenLoadingBarThunk(true));
        const result = await axiosGet(
            `/api/cleaning/date/cleaningDetailRoute?route_cleaning_id=${cleaningRouteList.route_cleaning_id}&&clickDate=${clickDate}`
        );
        dispatch(OpenLoadingBarThunk(false));
        if (result?.data?.success) {
            dispatch(
                initCleaningDetailRouteThunk(
                    result.data.cleaningDetailRecord
                )
            );
            setOpenCleaningRouteDialog(true);
        } else {
            dispatch(OpenSnackBarWithResultFail("未能取得清潔路線"));
        }
    }

    async function handleSwitchView(byContract: boolean) {
        setViewByContract(byContract);
        setSelectedContract(null);
        if (byContract && !contractFetched) {
            dispatch(OpenLoadingBarThunk(true));
            try {
                const result = await axiosGet(
                    `/api/cleaning/date/cleaningByContract?date=${clickDate}`
                );
                if (result?.data?.success) {
                    setContractList(result.data.contractList);
                } else {
                    dispatch(OpenSnackBarWithResultFail("未能取得合約清潔資料"));
                }
            } catch {
                dispatch(OpenSnackBarWithResultFail("未能取得合約清潔資料"));
            }
            setContractFetched(true);
            dispatch(OpenLoadingBarThunk(false));
        }
    }

    async function handleClickContractDetail(contractRow: ContractRow) {
        const contractGroup = contractList.find(c => c.contract_id === contractRow.contract_id);
        if (!contractGroup || contractGroup.records.length === 0) return;

        setContractDetailLoading(true);
        dispatch(OpenLoadingBarThunk(true));

        const routeIds = Array.from(new Set(contractGroup.records.map(r => r.route_cleaning_id)));
        const binIds = new Set(contractGroup.records.map(r => r.bin_id));

        let allRecords: any[] = [];
        for (const rid of routeIds) {
            try {
                const result = await axiosGet(
                    `/api/cleaning/date/cleaningDetailRoute?route_cleaning_id=${rid}&&clickDate=${clickDate}`
                );
                if (result?.data?.success) {
                    allRecords = allRecords.concat(result.data.cleaningDetailRecord);
                }
            } catch { }
        }

        const filtered = allRecords.filter((row: any) => binIds.has(row.bin_id));
        dispatch(initCleaningDetailRouteThunk(filtered));
        setSelectedContract(contractGroup);
        setContractDetailLoading(false);
        dispatch(OpenLoadingBarThunk(false));
    }

    function handleCloseDialog() {
        setOpenCleaningRouteDialog(false);
    }

    function handleBackToContractList() {
        setSelectedContract(null);
    }

    const routeColumns: GridColDef[] = [
        {
            field: "route_cleaning_id",
            headerName: "清潔路線編號",
            flex: 1,
        },
        {
            field: "route_name",
            headerName: "路線名稱",
            flex: 1,
        },
        {
            field: "username",
            headerName: "負責司機",
            flex: 1,
        },
        {
            field: "action",
            headerName: "路線詳情",
            flex: 1,
            renderCell: (data) => (
                <div>
                    <Button
                        variant="contained"
                        onClick={() => handleClickRouteCleaningDetail(data.row)}
                    >
                        <ButtonTextGrid>
                            <EditNote />
                            <Grid>詳情</Grid>
                        </ButtonTextGrid>
                    </Button>
                </div>
            ),
        },
    ];

    const contractColumns: GridColDef[] = [
        {
            field: "contract",
            headerName: "合約名稱",
            flex: 2,
        },
        {
            field: "bin_count",
            headerName: "廁所數量",
            flex: 1,
            type: "number",
        },
        {
            field: "record_count",
            headerName: "清潔紀錄",
            flex: 1,
            type: "number",
        },
        {
            field: "action",
            headerName: "合約詳情",
            flex: 1,
            renderCell: (data) => (
                <div>
                    <Button
                        variant="contained"
                        onClick={() => handleClickContractDetail(data.row)}
                    >
                        <ButtonTextGrid>
                            <EditNote />
                            <Grid>詳情</Grid>
                        </ButtonTextGrid>
                    </Button>
                </div>
            ),
        },
    ];

    const contractRows: ContractRow[] = contractList.map((group) => ({
        id: group.contract_id,
        contract_id: group.contract_id,
        contract: group.contract,
        record_count: group.records.length,
        bin_count: new Set(group.records.map((r) => r.bin_id)).size,
    }));

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={viewByContract}
                            onChange={(e) => handleSwitchView(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="按合約檢視"
                />
            </div>

            {!viewByContract ? (
                <div style={{ height: "900px", width: "100%" }}>
                    <DataGrid
                        rows={cleaningRouteList || []}
                        columns={routeColumns}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 30 },
                            },
                        }}
                        pageSizeOptions={[5, 10]}
                        checkboxSelection
                        getRowId={(row) => row.route_cleaning_id}
                    />
                </div>
            ) : !selectedContract ? (
                <div style={{ height: "900px", width: "100%" }}>
                    <DataGrid
                        rows={contractRows}
                        columns={contractColumns}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 30 },
                            },
                        }}
                        pageSizeOptions={[5, 10]}
                        getRowId={(row) => row.id}
                    />
                </div>
            ) : (
                <div>
                    <Button
                        variant="contained"
                        onClick={handleBackToContractList}
                        sx={{ mb: 1 }}
                    >
                        <ButtonTextGrid>
                            <ArrowBack sx={{ color: 'white' }} />
                            <Grid sx={{ color: 'white' }}>返回合約列表</Grid>
                        </ButtonTextGrid>
                    </Button>
                    <CleaningDetailRoute />
                </div>
            )}

            <FormDialog
                title="詳細情況"
                open={openDetailRouteDialog}
                handleClose={handleCloseDialog}
                size="xl"
            >
                <CleaningDetailRoute />
            </FormDialog>
        </div>
    );
}

export default CleaningRoute;
